import { css } from "@emotion/react";
import { useTranslations } from "next-intl";
import { ReactNode, useRef, useState } from "react";

import {
  IconButton,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Popover,
  Text,
  ToggleSwitch,
} from "@zapier/design-system";
import { useCurrentAccountId } from "@zapier/identity";

import { USER_INTERACTION_EVENT } from "@zapier/reporting/src/constants";
import { useEmitEvent, useHasHydrated } from "@zapier/reporting/src/hooks";
import { getResponsiveStyles } from "@zapier/reporting/src/utils";
import useAutoReplay from "./useAutoReplay";

const modalStyle = css`
  display: grid;
  grid-gap: 20px;
`;

const rootStyle = css`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 16px;
`;

const autoReplayHelpButtonStyle = css`
  border: none;
`;

const tipStyle = getResponsiveStyles({
  mobile: css`
    display: grid;
    grid-gap: 5px;
    text-align: left;
  `,
  desktop: css`
    width: 230px;
  `,
});

function AutoReplaySwitch() {
  const t = useTranslations("run-log.auto-replay-switch");
  const [showModal, setShowModal] = useState(false);
  const [showAutoplayPopover, setShowAutoplayPopover] = useState(false);
  const accountId = useCurrentAccountId();
  const hasHydrated = useHasHydrated();
  const autoreplayHelpIconRef = useRef(null);

  const {
    canAutoReplay,
    canChangeAutoReplay,
    error,
    isAutoReplayEnabled,
    isBusy,
    isLoading,
    onChange,
  } = useAutoReplay({ accountId });

  const { emitEvent } = useEmitEvent();

  if (error) {
    return (
      <Text color="StatusErrorWeaker" type="SmallPrint1">
        {error}
      </Text>
    );
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!canChangeAutoReplay) return;

    // We always allow you to turn it off in case you downgraded
    // and it's still on for some reason
    if (canAutoReplay || isAutoReplayEnabled) {
      onChange(e.currentTarget.checked);
      emitEvent({
        eventName: USER_INTERACTION_EVENT,
        meta: {
          event_action: "click",
          interaction_goal: "manage_autoplay_setting",
          interaction_name: "zap_runs_interaction",
          interaction_raw_text: `Autoreplay ${
            isAutoReplayEnabled ? "Off" : "On"
          }`,
          option_selected: isAutoReplayEnabled ? "off" : "on",
        },
      });
    } else {
      setShowModal(true);
    }
  }

  const tip = (
    <div css={tipStyle}>
      <Text type="SmallPrint1">
        <p>
          {t("tip", {
            state: !canChangeAutoReplay
              ? "owner_only"
              : isAutoReplayEnabled
                ? "enabled"
                : "disabled",
          })}
        </p>

        <br />
        <p>
          {t.rich("new-tip", {
            strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
          })}
        </p>
      </Text>
      <Link href="https://help.zapier.com/hc/en-us/articles/19220226086797-What-is-replay#h_01H9KB37H227CJ15FVPZXJVZ3P">
        {t("learn-more")}
      </Link>
    </div>
  );

  return (
    <>
      <div css={rootStyle}>
        <Text color="GrayWarm10" type="SmallPrint3">
          {t("label")}
        </Text>

        <div ref={autoreplayHelpIconRef}>
          <IconButton
            size="small"
            variant="ghost"
            name="navHelp"
            aria-label={t("help-aria-label")}
            aria-expanded={showAutoplayPopover}
            onPress={() => setShowAutoplayPopover(!showAutoplayPopover)}
            css={autoReplayHelpButtonStyle}
          />
        </div>

        <Popover
          color="light"
          isOpen={showAutoplayPopover && !isBusy}
          onOpenChange={setShowAutoplayPopover}
          title={t("popover-title", {
            isEnabled: String(isAutoReplayEnabled),
          })}
          triggerRef={autoreplayHelpIconRef}
        >
          {tip}
        </Popover>

        <ToggleSwitch
          aria-label={t("toggle-aria-label", {
            isEnabled: String(isAutoReplayEnabled),
          })}
          checked={isAutoReplayEnabled}
          disabled={!canChangeAutoReplay}
          id="autoreplay"
          isLoading={isLoading || !hasHydrated || isBusy}
          onChange={handleChange}
        />
      </div>
      <ModalDialogRoot
        aria-label={t("label")}
        isOpen={showModal}
        onOpenChange={setShowModal}
      >
        <ModalDialogHeader>{t("label")}</ModalDialogHeader>
        <ModalDialogBody>
          <div css={modalStyle}>
            <Text tag="h2" type="Body4">
              {t("upgrade-to-activate")}
            </Text>
            <Text tag="p" type="SmallPrint1">
              {t.rich("upgrade-description", {
                strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
              })}
            </Text>
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Link href="/app/billing/plans" variant="primary">
            {t("upgrade")}
          </Link>
        </ModalDialogFooter>
      </ModalDialogRoot>
    </>
  );
}

export default AutoReplaySwitch;
